import { RangeDirection } from './range_finder_session.ts';
import {
  announcementBubble,
  hopBubble,
  scrollOf,
  StairsBubble,
  StairsSceneModel,
  treadOf,
} from './range_stairs_model.ts';

// The stairs scene's choreography (issue #3510, addendum 3): the timeline behind the singer's hop,
// the endless stairs scrolling under him, the landing that closes a direction and the handover that
// carries him from the foot of the descent to the foot of the ascent. Pure: no React, no DOM, no
// timers and no pixels. It is fed events - which the direction screen derives by comparing one
// scene model with the last - and a reading of a monotonic clock, and it answers with the frame to
// draw. That is what makes the whole sequence testable without a browser, which the PRD asks for.
//
// Two things stay out of here. Wording lives in range_stairs_model.ts, so every line the singer
// says is decided in one place; geometry lives in range_stairs_scene.tsx, so the keyframe talks in
// treads, steps and fractions rather than in pixels.
//
// Every duration and easing below is the approved prototype's.

export interface StairsTimings {
  hopMs: number;
  pauseMs: number;
  fadeMs: number;
  slideMs: number;
  announceMs: number;
  arrowDrawMs: number;
  arrowFadeMs: number;
}

export const stairsTimings: StairsTimings = {
  hopMs: 520, // one tread, on an ease-out arc
  pauseMs: 1600, // how long "Great job!" stands on the landing
  fadeMs: 300, // the bubble leaving, and the announcement arriving
  slideMs: 800, // the handover: one rigid slide at one velocity
  announceMs: 1600, // "Now let's go higher!"
  arrowDrawMs: 700, // the arrow drawing itself on when a direction begins
  arrowFadeMs: 400, // and leaving on the first hop
};

// The arrow's head appears over the last fifth of the shaft, as the shaft arrives
export const arrowHeadFrom = 0.8;

// How long a bubble stands before another may replace it (issue #3510, addendum 4). Not a stage of
// the timeline above - the bubble stabilizer (range_bubble_stabilizer.ts) owns it - but a duration
// of the same scene clock, and a tunable of the same scene, so it lives beside the rest of them.
export const bubbleMinShowMs = 800;

// How long a direction's whole ending runs, from the landing hop to the moment the page may move
// on: the descent's also hands over to the ascent and announces it.
export function doneSequenceMs(handover: boolean, reducedMotion = false): number {
  const { hopMs, pauseMs, fadeMs, slideMs, announceMs } = timingsFor(reducedMotion);
  return hopMs + pauseMs + fadeMs + (handover ? slideMs + announceMs : 0);
}

// Under `prefers-reduced-motion: reduce` nothing travels: the hop, the scroll, the handover slide
// and the arrow's draw-on all take no time at all. The pauses are what the sequence is for, so
// they, and the opacity fades that are not motion, are kept exactly as they are.
function timingsFor(reducedMotion: boolean): StairsTimings {
  if (!reducedMotion) {
    return stairsTimings;
  }
  return { ...stairsTimings, hopMs: 0, slideMs: 0, arrowDrawMs: 0 };
}

// ---- Events ----------------------------------------------------------------------------------

// Where the scene rests between animations: which tread the singer stands on, how far the flight
// has scrolled, and how far it has moved away from the landing's far end to lengthen the platform
// (in steps, so the scene alone knows how wide a step is).
export interface StairsRest {
  tread: number;
  scroll: number;
  shift: number;
}

export type StairsEvent =
  // A direction began: the arrow draws itself on, unless the screen opened partway through one
  | { kind: 'directionBegan'; direction: RangeDirection; rest: StairsRest; arrowGone: boolean }
  // A note was credited: one tread along, scrolling the flight when the tread ahead would run out.
  // The note that ends the direction (the bound) arrives with the ending it owes, because the
  // machine credits it and ends the direction in one step: he hops, and then he lands.
  | { kind: 'hop'; fromTread: number; toTread: number; fromScroll: number; toScroll: number; ending?: StairsEnding }
  // The direction is over: onto the landing, which grows a step under him, and everything after
  | { kind: 'directionEnded'; fromTread: number; ending: StairsEnding };

// A direction's ending, wherever it is started from: the landing hop's destination, the scroll it
// keeps, and whether the ascent has to be handed over to afterwards.
export interface StairsEnding {
  toTread: number;
  scroll: number;
  handover: boolean;
}

function restOf(model: StairsSceneModel): StairsRest {
  return { tread: treadOf(model), scroll: scrollOf(model), shift: model.onLanding ? 1 : 0 };
}

function endingOf(model: StairsSceneModel): StairsEnding {
  return {
    toTread: treadOf(model),
    scroll: scrollOf(model),
    // Only the descent hands over; the ascent's ending runs into the result screen
    handover: model.direction === 'lower',
  };
}

// The one event, if any, that turns `previous` into `next`. The direction screen calls this on
// every render with the last two scene models, so the choreography never has to be told anything
// the state machine has not already said.
export function eventFor(previous: StairsSceneModel, next: StairsSceneModel): StairsEvent | undefined {
  if (previous.direction !== next.direction) {
    // The ascent beginning, which is also how the handover ends: a fresh flight, a fresh arrow
    return {
      kind: 'directionBegan',
      direction: next.direction,
      rest: restOf(next),
      arrowGone: next.notes.length > 0,
    };
  }
  const ended = next.onLanding && !previous.onLanding;
  if (next.notes.length > previous.notes.length) {
    return {
      kind: 'hop',
      fromTread: treadOf(previous),
      toTread: next.notes.length, // the new note's own tread, which is where the hop ends
      fromScroll: scrollOf(previous),
      toScroll: scrollOf(next),
      ending: ended ? endingOf(next) : undefined,
    };
  }
  if (ended) {
    return { kind: 'directionEnded', fromTread: treadOf(previous), ending: endingOf(next) };
  }
  return undefined;
}

// ---- The state machine -----------------------------------------------------------------------

// A hop, or the landing hop and the whole ending that follows it. Times are readings of the clock
// the caller passes to keyframeAt, so one monotonic clock drives everything.
interface StairsMove {
  ending: boolean;
  handover: boolean;
  startMs: number;
  fromTread: number;
  toTread: number;
  fromScroll: number;
  toScroll: number;
}

export interface StairsChoreography {
  direction: RangeDirection;
  reducedMotion: boolean;
  rest: StairsRest;
  arrow: {
    drawnAtMs: number; // when the draw-on began
    fadeAtMs?: number; // when the first hop started it leaving
    gone: boolean; // the screen opened on a direction already under way: no arrow at all
  };
  move?: StairsMove;
  // An ending owed while a hop is in flight - the note credited at the bound, or a stop tapped
  // inside the hop's half second. It begins the moment he lands, rather than snapping him there.
  owed?: StairsEnding;
}

// The scene standing where the model already has it, with the arrow drawing itself on if this is
// the top of a direction: everything a mount begins with except the ending it may owe.
function restingChoreography(model: StairsSceneModel, elapsedMs: number, reducedMotion: boolean): StairsChoreography {
  return {
    direction: model.direction,
    reducedMotion,
    rest: restOf(model),
    arrow: { drawnAtMs: elapsedMs, gone: model.notes.length > 0 || model.onLanding },
  };
}

// A scene that has just been mounted. A model that is already on the landing is a direction whose
// ending nobody has run yet - a screen opened on a done phase, or one remounted inside it - and
// the page is waiting on that ending to move the machine on, so the mount starts it from the tread
// he would have hopped off rather than standing him on the platform with nothing owed.
export function beginChoreography(
  model: StairsSceneModel,
  elapsedMs: number,
  reducedMotion = false,
): StairsChoreography {
  const resting = restingChoreography(model, elapsedMs, reducedMotion);
  if (!model.onLanding) {
    return resting;
  }
  return startEnding(
    resting,
    { toTread: resting.rest.tread, scroll: resting.rest.scroll, handover: model.direction === 'lower' },
    model.notes.length,
    elapsedMs,
  );
}

// The landing hop and everything after it, from `fromTread`
function startEnding(
  choreography: StairsChoreography,
  ending: StairsEnding,
  fromTread: number,
  elapsedMs: number,
): StairsChoreography {
  return {
    ...choreography,
    rest: { tread: ending.toTread, scroll: ending.scroll, shift: 1 },
    // The landing is a hop too: an arrow still standing (the singer gave up before a single
    // note) leaves on it, as it would have on any other
    arrow: { ...choreography.arrow, fadeAtMs: choreography.arrow.fadeAtMs ?? elapsedMs },
    owed: undefined,
    move: {
      ending: true,
      handover: ending.handover,
      startMs: elapsedMs,
      fromTread,
      toTread: ending.toTread,
      fromScroll: ending.scroll,
      toScroll: ending.scroll,
    },
  };
}

// Whether a hop is still in the air at `elapsedMs`, which is what decides between starting an
// ending now and owing it until he lands
function isMidHop(choreography: StairsChoreography, elapsedMs: number): boolean {
  const { move } = choreography;
  return move !== undefined && !move.ending && elapsedMs < move.startMs + timingsFor(choreography.reducedMotion).hopMs;
}

export function advance(choreography: StairsChoreography, event: StairsEvent, elapsedMs: number): StairsChoreography {
  switch (event.kind) {
    case 'directionBegan':
      return {
        direction: event.direction,
        reducedMotion: choreography.reducedMotion,
        rest: event.rest,
        arrow: { drawnAtMs: elapsedMs, gone: event.arrowGone },
      };
    case 'hop': {
      const hopped: StairsChoreography = {
        ...choreography,
        rest: { tread: event.toTread, scroll: event.toScroll, shift: 0 },
        // The arrow leaves on the first hop and never comes back, so a later hop must not restart it
        arrow: { ...choreography.arrow, fadeAtMs: choreography.arrow.fadeAtMs ?? elapsedMs },
        owed: event.ending,
        move: {
          ending: false,
          handover: false,
          startMs: elapsedMs,
          fromTread: event.fromTread,
          toTread: event.toTread,
          fromScroll: event.fromScroll,
          toScroll: event.toScroll,
        },
      };
      if (!event.ending) {
        return hopped;
      }
      // The note at the bound: he is credited with it, hops onto its tread, and only then does the
      // direction end under him. The rest is the landing's, because that is where he comes to a stop.
      return { ...hopped, rest: { tread: event.ending.toTread, scroll: event.ending.scroll, shift: 1 } };
    }
    case 'directionEnded': {
      if (!isMidHop(choreography, elapsedMs)) {
        return startEnding(choreography, event.ending, event.fromTread, elapsedMs);
      }
      // Tapped inside a hop's half second: he finishes the tread he is in the air over rather than
      // snapping back to it, and the landing move starts from there (see moveAt)
      return {
        ...choreography,
        rest: { tread: event.ending.toTread, scroll: event.ending.scroll, shift: 1 },
        owed: event.ending,
      };
    }
  }
}

// ---- The frame -------------------------------------------------------------------------------

// Where the scene is in the sequence. 'still' is everything that is not moving - which is most of a
// run - and the five after the landing hop are the direction's ending, in order.
export type StairsStage = 'still' | 'hop' | 'landing' | 'pause' | 'fade' | 'slide' | 'announce' | 'finished';

// The descent leaving and the ascent arriving, as one rigid slide: both flights are drawn, offset
// by the same distance in opposite directions, and cross-fade as they pass.
export interface StairsHandover {
  slide: number; // 0..1 of the distance that puts the singer on the far-left tread
  leaving: number; // the finished direction's opacity
  arriving: number; // the next one's
}

export interface StairsKeyframe {
  stage: StairsStage;
  tread: number; // fractional while he is in the air
  scroll: number; // fractional while the flight scrolls under him
  shift: number; // in steps: how far the flight has moved to lengthen the landing
  lift: number; // 0..1 of the hop's arc
  arrowGrow: number; // 0..1 of the shaft drawn
  arrowOpacity: number;
  bubbleOpacity: number;
  bubble?: StairsBubble; // said over whatever the state would otherwise say
  handover?: StairsHandover;
  animating: boolean; // another frame is worth drawing
}

// He is in the air: legs apart, and no note lit on the tread ahead. Both hops are one, the ordinary
// one and the landing's.
export function isHopping(stage: StairsStage): boolean {
  return stage === 'hop' || stage === 'landing';
}

// The whole ending is over: the page may move the machine on
export function isFinished(stage: StairsStage): boolean {
  return stage === 'finished';
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

// Fast off the tread and settling onto the next, which is how a hop reads
const easeOut = (t: number): number => 1 - (1 - t) ** 3;

// One velocity through the middle of the handover, easing off at both ends
const easeInOut = (t: number): number => (t < 0.5 ? 4 * t ** 3 : 1 - (-2 * t + 2) ** 3 / 2);

// A duration of zero (reduced motion) is over before it began
function progress(elapsedMs: number, durationMs: number): number {
  return durationMs <= 0 ? 1 : clamp01(elapsedMs / durationMs);
}

const lerp = (from: number, to: number, t: number): number => from + (to - from) * t;

// The scene at `elapsedMs`, a reading of the same clock the events were recorded on.
export function keyframeAt(choreography: StairsChoreography, elapsedMs: number): StairsKeyframe {
  const { arrow, rest, reducedMotion } = choreography;
  const timings = timingsFor(reducedMotion);
  const arrowGrow = arrow.gone ? 0 : progress(elapsedMs - arrow.drawnAtMs, timings.arrowDrawMs);
  const arrowOpacity = arrowOpacityAt(arrow, elapsedMs, timings);
  const arrowAnimating = !arrow.gone && (arrowGrow < 1 || (arrow.fadeAtMs !== undefined && arrowOpacity > 0));
  const frame = moveFrame(moveAt(choreography, timings, elapsedMs), rest, choreography.direction, elapsedMs, timings);
  return {
    ...frame,
    arrowGrow,
    arrowOpacity,
    animating: frame.animating || arrowAnimating,
  };
}

function arrowOpacityAt(arrow: StairsChoreography['arrow'], elapsedMs: number, timings: StairsTimings): number {
  if (arrow.gone) {
    return 0;
  }
  if (arrow.fadeAtMs === undefined) {
    return 1;
  }
  return 1 - progress(elapsedMs - arrow.fadeAtMs, timings.arrowFadeMs);
}

// The scene standing still, which is what most of a run looks like
type MoveFrame = Omit<StairsKeyframe, 'arrowGrow' | 'arrowOpacity'>;

function stillFrame(rest: StairsRest, stage: StairsStage): MoveFrame {
  return {
    stage,
    tread: rest.tread,
    scroll: rest.scroll,
    shift: rest.shift,
    lift: 0,
    bubbleOpacity: 1,
    animating: false,
  };
}

// The move in effect at `elapsedMs`: the hop, or, once he has landed on the tread it was for, the
// ending that hop owes (the bound's note, or a stop tapped while he was in the air).
function moveAt(choreography: StairsChoreography, timings: StairsTimings, elapsedMs: number): StairsMove | undefined {
  const { move, owed } = choreography;
  if (!move || !owed) {
    return move;
  }
  const landedMs = move.startMs + timings.hopMs;
  if (elapsedMs < landedMs) {
    return move;
  }
  return {
    ending: true,
    handover: owed.handover,
    startMs: landedMs,
    fromTread: move.toTread,
    toTread: owed.toTread,
    fromScroll: owed.scroll,
    toScroll: owed.scroll,
  };
}

function moveFrame(
  move: StairsMove | undefined,
  rest: StairsRest,
  direction: RangeDirection,
  elapsedMs: number,
  timings: StairsTimings,
): MoveFrame {
  if (!move) {
    return stillFrame(rest, 'still');
  }
  const sinceMs = elapsedMs - move.startMs;
  const { hopMs, pauseMs, fadeMs, slideMs, announceMs } = timings;
  if (sinceMs < hopMs) {
    // The arc is linear in time while the travel eases out, so he is highest at the half-way point
    // rather than over the tread he is leaving
    const t = progress(sinceMs, hopMs);
    const eased = easeOut(t);
    return {
      stage: move.ending ? 'landing' : 'hop',
      tread: lerp(move.fromTread, move.toTread, eased),
      scroll: lerp(move.fromScroll, move.toScroll, eased),
      shift: move.ending ? eased : 0,
      lift: Math.sin(Math.PI * t),
      // The landing hop needs no line of its own: the state already says "Great job!"
      bubble: move.ending ? undefined : hopBubble(direction),
      bubbleOpacity: 1,
      animating: true,
    };
  }
  if (!move.ending) {
    return stillFrame(rest, 'still');
  }
  const pauseEnd = hopMs + pauseMs;
  const fadeEnd = pauseEnd + fadeMs;
  if (sinceMs < pauseEnd) {
    return { ...stillFrame(rest, 'pause'), animating: true };
  }
  if (sinceMs < fadeEnd) {
    // "Great job!" leaves before anything moves again
    return { ...stillFrame(rest, 'fade'), bubbleOpacity: 1 - progress(sinceMs - pauseEnd, fadeMs), animating: true };
  }
  if (!move.handover) {
    return stillFrame(rest, 'finished');
  }
  const slideEnd = fadeEnd + slideMs;
  if (sinceMs < slideEnd) {
    const slide = easeInOut(progress(sinceMs - fadeEnd, slideMs));
    return {
      ...stillFrame(rest, 'slide'),
      bubbleOpacity: 0,
      handover: { slide, leaving: 1 - slide, arriving: slide },
      animating: true,
    };
  }
  const announceEnd = slideEnd + announceMs;
  if (sinceMs < announceEnd) {
    return {
      ...stillFrame(rest, 'announce'),
      // The announcement fades in exactly as "Great job!" faded out
      bubbleOpacity: progress(sinceMs - slideEnd, fadeMs),
      bubble: announcementBubble,
      handover: { slide: 1, leaving: 0, arriving: 1 },
      animating: true,
    };
  }
  return { ...stillFrame(rest, 'finished'), handover: { slide: 1, leaving: 0, arriving: 1 } };
}

// The frame a scene draws when nothing is animating it: a story, or a screen that has not moved
// since it mounted. Nothing is owed here, not even by a model on the landing: this is the scene as
// a picture, and the ending a mounted screen would run belongs to beginChoreography.
export function stillKeyframe(model: StairsSceneModel): StairsKeyframe {
  // Past the draw-on, so the arrow of a direction that has not started yet is whole rather than
  // caught mid-stroke
  return keyframeAt(restingChoreography(model, 0, false), stairsTimings.arrowDrawMs);
}
